import type { CharacterProfile } from '~/types'
/**
 * 角色页面相关API接口
 * 使用 mateHttp 封装请求
 */
import { mateHttp } from '~/utils/mate/mateRequest'

// 标签数据接口
export interface LabelItem {
    code: string
    text: string
    color: string
}

// 角色图片/视频详情接口
export interface FigureDetail {
    figure: string
    video: boolean
}

// 角色信息接口
export interface MateInfo {
    mate_id: number
    name: string
    mate_category: string
    figure_type: string
    introduce: string
    labels: LabelItem[]
    figure_images: string[]
    figure_details: FigureDetail[]
    verbal_tics: string
    bgm: string
    lang: string
    world_name?: string
    like_count: number
    psy_gender: number
    badge_icon: string
    score: number
    age?: string
    avatar?: string // 头像字段
}

// 探索项接口
export interface ExploreItem {
    explore_type: string
    explore_type_desc: string
    mate_info: MateInfo
    hot: boolean
}

// 角色页面响应接口
export interface DigitalHumanResponse {
    explore_list: ExploreItem[]
    offset: string
    page_cursor: string
    has_next: boolean
}

// 角色数据接口（适配数据结构）
export interface CharacterItem {
    id: number
    name: string
    avatar: string // 主图/背景图
    avatarIcon?: string // 圆形头像（用于悬浮状态）
    description: string
    category: string
    tags: LabelItem[]
    isNew?: boolean
    gender?: 'male' | 'female'
    psy_gender?: number // 性别：1=男，0=女，-1=未知
    world_name?: string
    like_count: number
    score: number
    verbal_tics: string
    figure_type: string
    is_video?: boolean
    isLiked?: boolean
    isDisliked?: boolean
    age?: string
    selected?: boolean
    match_mate_id?: number
}

// 角色列表响应接口（适配数据结构）
export interface CharacterListResponse {
    list: CharacterItem[]
    total: number
    offset: string
    page_cursor: string
    pageSize: number
    hasMore: boolean
}

/**
 * 获取角色页面原始数据
 * @param params 查询参数
 * @returns 角色页面数据
 */
export function getDigitalHumanData(params?: {
    offset?: string
    page_cursor?: string
    page_size?: number
    recommend_type?: string // 推荐类型：RECOMMEND | LATEST
    gender?: string // 性别筛选：ALL | FEMALE | MALE
    labels?: string // 兴趣标签：逗号分隔的字符串，如 "ANIME,GAME,OC"（选中ALL时不传）
}) {
    return mateHttp.get<DigitalHumanResponse>('/api/v1/h5/roles', params)
}

/**
 * 获取角色列表数据（统一方法，可在任何地方调用）
 * @param currentOffset 当前偏移量
 * @param pageSize 每页大小
 * @param currentPageCursor 当前页面游标
 * @param recommendType 推荐类型
 * @param gender 性别筛选
 * @param labels 兴趣标签数组
 * @returns Promise<CharacterListResponse | null>
 */
export const getCharacterList = async (
    currentOffset = '0',
    pageSize = 10,
    currentPageCursor = '',
    recommendType?: string,
    gender?: string,
    labels?: string // 函数参数接收字符串（逗号分隔的标签）
): Promise<CharacterListResponse | null> => {
    try {
        const params: {
            page_size: number
            offset?: string
            page_cursor?: string
            recommend_type?: string
            gender?: string
            labels?: string
        } = {
            page_size: pageSize,
        }

        // 添加offset参数
        if (currentOffset !== '0') {
            params.offset = currentOffset
        }

        // 添加page_cursor参数
        if (currentPageCursor) {
            params.page_cursor = currentPageCursor
        }

        // 添加推荐类型
        if (recommendType) {
            params.recommend_type = recommendType
        }

        // 添加性别筛选
        if (gender) {
            params.gender = gender
        }

        // 添加兴趣标签（只有选中非ALL标签时才传递）
        if (labels) {
            params.labels = labels
        }

        // 调用 mateHttp.get
        const response = await getDigitalHumanData(params)

        const digitalHumanData = response.data
        if (!digitalHumanData) {
            return null
        }

        // 检查 explore_list 是否存在，如果不存在或为空数组，返回空结果
        if (
            !digitalHumanData.explore_list ||
            !Array.isArray(digitalHumanData.explore_list)
        ) {
            console.log('个性化数据为空，返回空列表')
            return {
                list: [],
                total: 0,
                offset: digitalHumanData.offset || '0',
                page_cursor: digitalHumanData.page_cursor || '',
                pageSize,
                hasMore: digitalHumanData.has_next || false,
            }
        }

        // 转换数据格式以适配现有组件
        const characterList: CharacterItem[] =
            digitalHumanData.explore_list.map(item => {
                const mateInfo = item.mate_info
                const figureDetail = mateInfo.figure_details[0] || {
                    figure: '',
                    video: false,
                }

                return {
                    id: mateInfo.mate_id,
                    name: mateInfo.name,
                    avatar: figureDetail.figure, // 主图/背景图
                    avatarIcon: mateInfo.avatar, // 圆形头像
                    description: mateInfo.introduce,
                    category: mateInfo.mate_category,
                    tags: mateInfo.labels,
                    world_name: mateInfo.world_name,
                    like_count: mateInfo.like_count,
                    score: mateInfo.score,
                    verbal_tics: mateInfo.verbal_tics,
                    figure_type: mateInfo.figure_type,
                    is_video: figureDetail.video,
                    isNew: item.hot, // 使用hot字段作为isNew
                    age: mateInfo.age,
                    psy_gender: mateInfo.psy_gender, // 性别：1=男，0=女，-1=未知
                }
            })

        return {
            list: characterList,
            total: characterList.length,
            offset: digitalHumanData.offset,
            page_cursor: digitalHumanData.page_cursor,
            pageSize,
            hasMore: digitalHumanData.has_next,
        }
    } catch (err) {
        console.error('获取角色页面数据异常:', err)
        return null
    }
}

/**
 * 角色匹配请求参数接口
 */
export interface MatchRoleParams extends Record<string, unknown> {
    match_mate_id: number
    match_type: 'LIKE' | 'DISLIKE'
    my_mate_id?: number
}

/**
 * 角色匹配响应接口
 */
export interface MatchRoleResponse {
    // 根据实际返回结构定义
    success?: boolean
    message?: string
    my_mate_id?: number
}

/**
 * 角色匹配操作（喜欢/不喜欢）
 * @param params 匹配参数
 * @returns Promise<MatchRoleResponse>
 */
export function matchRole(params: MatchRoleParams) {
    return mateHttp.post<MatchRoleResponse>(
        '/api/v1/h5/roles/auth/tab/match',
        params
    )
}

/**
 * 获取角色资料详情
 *
 * @param id - 角色ID (mate_id)
 * @returns 返回角色资料数据，失败时返回 null
 */
export const getCharacterProfile = async (
    id: string
): Promise<CharacterProfile | null> => {
    try {
        const response = await mateHttp.get<CharacterProfile>(
            '/api/v1/h5/roles/info',
            {
                match_mate_id: id,
            }
        )

        // 检查响应状态
        if (response.status !== 200 && response.status !== 0) {
            return null
        }

        return response.data || null
    } catch (error) {
        return null
    }
}

interface MediaDetail {
    cover_image: string
    media: string
    video: boolean
}
interface PostItem {
    text: string
    media_details: MediaDetail[]
}
interface MomentsItems {
    moment_id: number
    post: PostItem
}
interface MateMoment {
    create_time_desc: string
    new_year: boolean
    moments_items: MomentsItems[]
}
interface MateMomentsResponse {
    has_next: boolean
    offset_id: string
    moments: MateMoment[]
}
/**
 * 获取动态列表数据
 * @param currentOffset 当前偏移量
 * @param pageSize 每页大小
 * @param currentPageCursor 当前页面游标
 * @returns Promise<CharacterListResponse | null>
 */
export const getMateMomentsUsingGET = async (
    currentOffset = '0',
    id: string
) => {
    try {
        const params: {
            mate_id: string
            offset_id?: string
        } = {
            mate_id: id,
        }
        if (currentOffset !== '0') {
            params.offset_id = currentOffset
        }
        const response = await mateHttp.get<MateMomentsResponse>(
            '/api/v1/h5/roles/auth/moments',
            params
        )
        // 检查响应状态
        if (response.status !== 200 && response.status !== 0) {
            console.error('获取动态失败:', response.message)
            return null
        }
        console.log('moments---', response)
        // 返回响应数据
        return response
    } catch (e) {
        console.error('获取动态异常-----:', e)
        return null
    }
}
